import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../../../utils/ag-grid-columns-utils.js";
import {LanesCaptions, LanesListIcons} from "./lanes-list-ui.jsx";
import {applyInitialSort, statusComparator} from "../../../utils/lanes-list-utils.js";
import {ImageTypeEnum, OcrEnum} from "../../../utils/lanes-utils.js";
import Button from "../../../../../../components/button/button.jsx";
import styles from "./lanes-table.module.css";
import LastPassageCell from "./last-passage-cell.jsx";
import {FaPen} from "react-icons/fa";
import ShowHideColumnsGrid from "../../../../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";

const headerVerticalClass = "[writing-mode:vertical-lr] [cursor:help]";
const centeredDiv = "h-full w-full flex items-center justify-center";
const COLUMN_SIZE_STORAGE_KEY = "lanes-table-column-sizes";
const storageItem = "lanes-hidden-columns";

const LanesTable = ({data, gridRef, setId, setShowModal, setIdLastPassage, setOpenLastPassage}) => {
    const [columnDefs, setColumnDefs] = useState([]);
    const hasLoadedColumnSizesRef = useRef(false);

    const toggleColumn = (field) => {
        setColumnDefs((prevDefs) =>
            prevDefs.map((col) => col.field === field ? {...col, hide: !col.hide} : col)
        );
    };

    const onColumnMoved = (params) => {
        const columns = params.api.getAllDisplayedColumns().map(col => col.colId);
        localStorage.setItem("lanes-order-columns", JSON.stringify(columns));
    };

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") {
            toggleColumn(column.colDef.field);
        }
    };

    const getSavedColumnSizes = useCallback(() => {
        try {
            const columnSizes = JSON.parse(localStorage.getItem(COLUMN_SIZE_STORAGE_KEY));

            if (!Array.isArray(columnSizes)) return [];

            return columnSizes
                .filter(column => column?.colId && Number.isFinite(column.width))
                .map(({colId, width}) => ({colId, width, flex: null}));
        } catch {
            return [];
        }
    }, []);

    const saveColumnSizes = useCallback((params) => {
        if (!hasLoadedColumnSizesRef.current || !params.finished) return;

        try {
            const columnSizes = params.api.getColumnState().map(({colId, width}) => ({
                colId,
                width,
            }));

            localStorage.setItem(COLUMN_SIZE_STORAGE_KEY, JSON.stringify(columnSizes));
        } catch {
            return;
        }
    }, []);

    useEffect(() => {
        const lastPassageComparator = (a, b, nodeA, nodeB, isDescending) => {
            const timeA = new Date(a).getTime();
            const timeB = new Date(b).getTime();
            const isEmptyA = !a || Number.isNaN(timeA);
            const isEmptyB = !b || Number.isNaN(timeB);

            if (isEmptyA && isEmptyB) return 0;
            if (isEmptyA) return isDescending ? -1 : 1;
            if (isEmptyB) return isDescending ? 1 : -1;

            return timeA - timeB;
        };

        const columns = [
            { headerName: "Status", field: "status", width: 40, headerClass: headerVerticalClass, sortable: true, filter: true,
                cellRenderer: ({data}) => {
                    let smartCapture = false;
                    if (data?.status === "alert") {
                        if (!data.smartCapturePin) smartCapture = true;
                        if (!data.smartCaptureDriver) smartCapture = true;
                    }

                    if (!data?.status) {
                        return <div className={centeredDiv}><LanesListIcons/></div>;
                    }

                    if (data.status && !smartCapture) {
                        return <div className={centeredDiv}><LanesListIcons icon="statusTrue"/></div>;
                    }

                    return <div className={centeredDiv}><LanesListIcons icon="statusAlert"/></div>;
                },
                comparator: (a, b) => statusComparator(a, b),
                tooltipValueGetter: ({data}) => {
                    if (data?.status === "alert") {
                        let smartCaptureTooltip = "";
                        if (!data.smartCapturePin) smartCaptureTooltip = "Nenhum smartcapture associado";
                        if (!data.smartCaptureDriver) smartCaptureTooltip = "Dispositivo de captura não configurado";
                        return smartCaptureTooltip;
                    }

                    if (!data?.status) return "Câmera inativa";
                    return "";
                },
            },
            { headerName: "ID", field: "id", width: 54, sortable: true, filter: true },
            { headerName: "Ponto de coleta", field: "capturePlace", flex: 2, headerTooltip: "Ponto de coleta", tooltipField: "capturePlace", sortable: true, filter: true },
            { headerName: "Sentido", field: "route", flex: 2, headerTooltip: "Sentido", tooltipField: "route", sortable: true, filter: true },
            { headerName: "Nome da câmera", field: "laneDescription", flex: 1, headerTooltip: "Nome da câmera", tooltipField: "laneDescription", sortable: true, filter: true },
            { headerName: "Observação", field: "laneObservation", flex: 1, headerTooltip: "Observação", tooltipField: "laneObservation", sortable: true, filter: true },
            { headerName: "Tipo de dispositivo", field: "type", flex: 1, headerTooltip: "Tipo de dispositivo", tooltipField: "type", sortable: true, filter: true },
            { headerName: "Smartcapture", field: "smartcapture", flex: 1, headerTooltip: "Smartcapture", tooltipField: "smartcapture" },
            { headerName: "Resp. Manutenção", field: "maintenanceGroup", flex: 1, headerTooltip: "Responsável pela manutenção", tooltipField: "maintenanceGroup", sortable: true, filter: true },
            { headerName: "Data última passagem", field: "dtLastPassage", flex: 1, headerTooltip: "Data última passagem", sortable: true,
                comparator: lastPassageComparator,
                cellRenderer: LastPassageCell,
                cellRendererParams: {
                    setIdLastPassage,
                    setOpenLastPassage
                }
            },
            { headerName: "Tempo off NOC", field: "nocTime", width: 40, headerClass: headerVerticalClass },
            { headerName: "Archiver", field: "archiver", width: 40, headerClass: headerVerticalClass },
            { headerName: "OCR", field: "ocr", width: 40, headerClass: headerVerticalClass,
                cellRenderer: ({value}) => (
                    <div className={centeredDiv}><LanesListIcons icon={value}/></div>
                ),
                tooltipValueGetter: ({value}) => {
                    if (value === OcrEnum.SUPPLEMENT) return "Complementar";
                    if (value === OcrEnum.OVERWRITE) return "Sobrepor";
                    return "Desligado";
                }
            },
            { headerName: "Ruminação", field: "rumination", width: 40, headerClass: headerVerticalClass,
                cellRenderer: ({value}) => <div className={centeredDiv}><LanesListIcons icon={value}/></div>
            },
            { headerName: "Classificação", field: "classification", width: 40, headerClass: headerVerticalClass,
                cellRenderer: ({value}) => <div className={centeredDiv}><LanesListIcons icon={value}/></div>
            },
            { headerName: "Imagens", field: "images", width: 40, headerClass: headerVerticalClass,
                tooltipValueGetter: ({value}) => {
                    if (value === ImageTypeEnum.TRAFFIC) return "Infração";
                    if (value === ImageTypeEnum.DEMAND) return "Sob demanda";
                    if (value === ImageTypeEnum.ALL) return "Todas";
                    return "Nenhuma";
                },
                cellRenderer: ({value}) => <div className={centeredDiv}><LanesListIcons icon={value}/></div>
            },
            { headerName: "Associações", field: "associations", headerTooltip: "Associação de câmeras de vídeo", width: 40, headerClass: headerVerticalClass,
                cellRenderer: ({value}) => <div className={centeredDiv}><LanesListIcons icon={value}/></div>
            },
            { headerName: "Editar", field: "actions", width: 50, headerClass: headerVerticalClass, headerComponent: () => null,
                tooltipValueGetter: () => "Editar",
                cellRenderer: ({data}) => (
                    <div className={centeredDiv}>
                        <Button className="h-[25px]! w-[30px]! !p-0" onClick={() => {
                            setId(data?.id);
                            setShowModal(true);
                        }}>
                            <FaPen/>
                        </Button>
                    </div>
                )
            },
        ];

        const defaultHiddenColumns = [];
        const hiddenColumns = JSON.parse(localStorage.getItem(storageItem)) ?? defaultHiddenColumns;
        const orderColumns = JSON.parse(localStorage.getItem("lanes-order-columns"));

        if (hiddenColumns) {
            columns.forEach(c => {
                if (hiddenColumns.includes(c.field)) c.hide = true;
            });
        }

        if (orderColumns && aggridUtils?.sortColumns) {
            aggridUtils.sortColumns(columns, orderColumns);
        }

        setColumnDefs(columns);
    }, [setId, setIdLastPassage, setOpenLastPassage, setShowModal]);

    return (
        <div className={["h-full flex flex-col", styles.custom].join(" ")}>
            <AgGridReact
                ref={gridRef}
                rowData={data ?? []}
                columnDefs={columnDefs}
                onGridReady={(params) => {
                    if (gridRef) gridRef.current = params.api;
                    applyInitialSort(params.api);

                    const columnSizes = getSavedColumnSizes();

                    if (columnSizes.length) {
                        params.api.applyColumnState({
                            state: columnSizes,
                            applyOrder: false,
                        });
                    }

                    hasLoadedColumnSizesRef.current = true;
                }}
                onColumnResized={saveColumnSizes}
                onColumnMoved={onColumnMoved}
                onColumnVisible={onColumnVisible}
                tooltipShowDelay={400}
                tooltipShowMode="standard"
                getRowId={({data: row}) => String(row.id)}
                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                animateRows={false}
                rowSelection={{
                    mode: 'singleRow',
                    checkboxes: false,
                    headerCheckbox: false,
                    enableClickSelection: true
                }}
                suppressCellFocus={true}
                gridOptions={{localeText: aggridUtils.localization()}}
                theme={aggridUtils.generalTheme({headerHeight: 128})}
            />

            <ShowHideColumnsGrid
                columnDefs={columnDefs}
                toggleColumn={toggleColumn}
                storageItem={storageItem}
            />

            <div className={styles.customBorder}>
                <LanesCaptions />
            </div>
        </div>
    );
};

export default LanesTable;